import React, { useState, useEffect } from "react";
import {
    Box,
    Button,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
    Tooltip,
    TableSortLabel,
    Snackbar,
    Alert,
    TablePagination,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions
} from "@mui/material";
import {
    Add as AddIcon,
    Visibility as VisibilityIcon,
    Person as PersonIcon,
    Edit as EditIcon,
    Group as GroupIcon,
    Close as CloseIcon,
    People as PeopleIcon,
    Print as PrintIcon
} from "@mui/icons-material";
import { useNavigate } from 'react-router-dom';
import api from '../../services/api.js';
import Preloader from '../../components/Preloader';
import { formatarTelefone, formatarCPF } from '../../utils/formatadores.js';
import CadastroBeneficiario from './CadastroBeneficiario';
import { useAuth } from '../../hooks/useAuth.js';

export default function ListagemBeneficiarios() {
    const navigate = useNavigate();
    const [beneficiarios, setBeneficiarios] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);

    // Controle do Dialog de Cadastro
    const [openCadastro, setOpenCadastro] = useState(false);
    const [beneficiarioEditarId, setBeneficiarioEditarId] = useState(null);
    const [openView, setOpenView] = useState(false);
    const [beneficiarioVisualizar, setBeneficiarioVisualizar] = useState(null);

    const [filtros, setFiltros] = useState({
        st_nome: "",
        st_cpf: "",
    });

    const [order, setOrder] = useState("asc");
    const [orderBy, setOrderBy] = useState("st_nome");
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    useEffect(() => {
        carregarBeneficiarios();
    }, []);

    const carregarBeneficiarios = async () => {
        setLoading(true);
        try {
            const response = await api.get('/beneficiario');
            setBeneficiarios(response.data.dados || []);
        } catch (error) {
            console.error('Erro ao carregar beneficiarios:', error);
            setError('Erro ao carregar beneficiarios');
        } finally {
            setLoading(false);
        }
    };

    const handleFiltroChange = (event) => {
        const { name, value } = event.target;
        setFiltros(prev => ({ ...prev, [name]: value }));
        setPage(0);
    };

    const handleRequestSort = (property) => {
        const isAsc = orderBy === property && order === "asc";
        setOrder(isAsc ? "desc" : "asc");
        setOrderBy(property);
    };

    const handleOpenCadastro = (id = null) => {
        setBeneficiarioEditarId(id);
        setOpenCadastro(true);
    };

    const handleCloseCadastro = () => {
        setOpenCadastro(false);
        setBeneficiarioEditarId(null);
    };

    const handleSaveSuccess = (message) => {
        setSuccess(message);
        carregarBeneficiarios();
        handleCloseCadastro();
    };

    const visualizarBeneficiario = (beneficiario) => {
        setBeneficiarioVisualizar(beneficiario);
        setOpenView(true);
    };

    const fecharVisualizacao = () => {
        setOpenView(false);
        setBeneficiarioVisualizar(null);
    };

    const { user } = useAuth(); // Access user context

    const handlePrint = async (beneficiario) => {
        try {
            const [templateRes, empresaRes] = await Promise.all([
                api.get('/contrato-template'),
                user?.id_empresa || user?.idempresa ? api.get(`/empresa/${user?.id_empresa || user?.idempresa}`) : Promise.resolve({ data: {} })
            ]);

            const templates = templateRes.data.dados || [];
            const template = templates.find(t => t.st_titulo.includes('Beneficiário'));
            const empresaData = empresaRes.data?.dados || empresaRes.data || {};
            const logoUrl = empresaData.st_logotipo || '';

            if (!template) {
                alert('Modelo de contrato de beneficiário não encontrado.');
                return;
            }

            let conteudo = template.st_conteudo_html;

            // Formatar Data Atual
            const hoje = new Date();
            const dia = String(hoje.getDate()).padStart(2, '0');
            const mes = String(hoje.getMonth() + 1).padStart(2, '0');
            const ano = hoje.getFullYear();
            const dataAtual = `${dia}/${mes}/${ano}`;

            // Helper para destacar campos
            const highlight = (text) => `<span class="highlight">${text}</span>`;

            // Substituir Placeholders com destaque
            conteudo = conteudo.replace(/{{nome}}/g, highlight(beneficiario.usuario?.st_nome || 'N/A'));
            conteudo = conteudo.replace(/{{cpf}}/g, highlight(formatarCPF(beneficiario.st_cpf) || 'N/A'));
            conteudo = conteudo.replace(/{{data_nascimento}}/g, highlight(beneficiario.dt_nascimento ? new Date(beneficiario.dt_nascimento).toLocaleDateString('pt-BR') : 'N/A'));

            const enderecoCompleto = `${beneficiario.st_logradouro || ''}, ${beneficiario.st_numero || ''} - ${beneficiario.st_bairro || ''}`;
            conteudo = conteudo.replace(/{{endereco}}/g, highlight(enderecoCompleto));

            conteudo = conteudo.replace(/{{cidade}}/g, highlight(beneficiario.st_cidade || 'Mogi das Cruzes'));
            conteudo = conteudo.replace(/{{data_atual}}/g, highlight(dataAtual));

            // Placeholders Padrão
            conteudo = conteudo.replace(/{{nome_plano}}/g, highlight('Plano Padrão'));
            conteudo = conteudo.replace(/{{valor_plano}}/g, highlight('A Consultar'));

            const win = window.open('', '_blank');
            win.document.write(`
                <html>
                    <head>
                        <title>Contrato - ${beneficiario.usuario?.st_nome}</title>
                        <link href="https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;700&family=Lato:wght@300;400;700&display=swap" rel="stylesheet">
                        <style>
                            @page {
                                size: A4;
                                margin: 0;
                            }
                            body { 
                                font-family: 'Lato', sans-serif;
                                font-size: 11pt;
                                color: #333;
                                line-height: 1.6;
                                margin: 0;
                                padding: 0.5cm;
                                box-sizing: border-box;
                            }

                            /* Container Principal com Borda e Cantos Arredondados */
                            .main-frame {
                                border: 2px solid #20ac9c; /* Use logo primary color */
                                border-radius: 20px;
                                padding: 40px 60px; /* Increased side padding */
                                min-height: 95vh;
                                position: relative;
                                margin: 0 auto;
                                box-sizing: border-box; /* Ensure padding is calculated inside */
                            }

                            /* Header Setup */
                            .header {
                                display: flex;
                                align-items: flex-start;
                                justify-content: space-between;
                                margin-bottom: 5px; /* Reduced margin */
                                border-bottom: 3px solid #D4AF37; /* Gold Accent */
                                padding-bottom: 10px; /* Reduced padding */
                            }
                            .header-logo img {
                                height: 130px; /* Increased logo size */
                                object-fit: contain;
                            }
                            
                            /* Body Styling */
                            .contract-body {
                                text-align: justify;
                                white-space: pre-wrap;
                                overflow-wrap: break-word; /* Prevent duplicate overflow */
                                word-break: break-word; /* Ensure long words break */
                            }
                            .contract-body > *:first-child {
                                margin-top: 0;
                            }
                            
                            /* Gradient Bar Top/Bottom of Frame (Optional Visual) */
                            .frame-decoration-top {
                                height: 8px;
                                background: linear-gradient(90deg, #20ac9c 0%, #D4AF37 100%);
                                border-radius: 6px 6px 0 0;
                                margin: -30px -40px 30px -40px; /* Pull to edges */
                            }

                            /* Typography Enhancements */
                            strong, b {
                                color: #000;
                                font-weight: 700;
                            }
                            
                            /* Highlighted Fields */
                            .highlight {
                                font-weight: bold;
                                color: #20ac9c; /* Teal Text */
                                border-bottom: 1px dotted #333;
                                padding: 0 2px;
                            }

                            /* Signatures */
                            .signature-section {
                                margin-top: 50px;
                                break-inside: avoid;
                            }
                        </style>
                    </head>
                    <body>
                        <div class="main-frame">
                            <div class="frame-decoration-top"></div>
                            
                            <div class="header">
                                <div class="header-logo">
                                    ${logoUrl ? `<img src="${logoUrl}" alt="Logo" />` : ''}
                                </div>
                            </div>

                            <div class="contract-body">
                                ${conteudo}
                            </div>
                        </div>

                        <script>
                            window.onload = function() { 
                                setTimeout(() => window.print(), 800); 
                            }
                        </script>
                    </body>
                </html>
            `);
            win.document.close();

        } catch (err) {
            console.error(err);
            setError("Erro ao gerar impressão.");
        }
    };

    // Filtragem e Ordenação
    const beneficiariosFiltrados = beneficiarios
        .filter((beneficiario) => {
            const nomeMatch = !filtros.st_nome ||
                beneficiario.usuario?.st_nome?.toLowerCase().includes(filtros.st_nome.toLowerCase());
            const cpfMatch = !filtros.st_cpf ||
                beneficiario.st_cpf?.includes(filtros.st_cpf.replace(/\D/g, ''));

            return nomeMatch && cpfMatch;
        })
        .sort((a, b) => {
            let aValue = "";
            let bValue = "";

            if (orderBy === 'st_nome') {
                aValue = a.usuario?.st_nome || "";
                bValue = b.usuario?.st_nome || "";
            } else {
                aValue = a[orderBy] || "";
                bValue = b[orderBy] || "";
            }

            if (typeof aValue === 'string') {
                aValue = aValue.toLowerCase();
                bValue = bValue.toLowerCase();
            }

            if (order === "asc") {
                return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
            } else {
                return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
            }
        });

    return (
        <Box sx={{ p: { xs: 2, sm: 3 }, height: '100vh', overflow: 'auto' }}>
            <Snackbar
                open={!!error}
                autoHideDuration={6000}
                onClose={() => setError(null)}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert onClose={() => setError(null)} severity="error" sx={{ width: '100%' }}>
                    {error}
                </Alert>
            </Snackbar>

            <Snackbar
                open={!!success}
                autoHideDuration={6000}
                onClose={() => setSuccess(null)}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert onClose={() => setSuccess(null)} severity="success" sx={{ width: '100%' }}>
                    {success}
                </Alert>
            </Snackbar>

            <Box sx={{
                margin: '0 1.5% 1.5% 1.5%',
                height: '90vh',
                display: 'flex',
                flexDirection: 'column'
            }}>
                {/* Header */}
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: { xs: 2, sm: 3 },
                        flexDirection: { xs: 'column', sm: 'row' },
                        gap: 2
                    }}
                >
                    <Typography
                        variant="h5"
                        sx={{
                            fontSize: { xs: '1.5rem', sm: '2rem' },
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1
                        }}
                    >
                        <PeopleIcon sx={{
                            fontSize: { xs: '1.5rem', sm: '2rem' },
                            color: '#20ac9c'
                        }} />
                        Beneficiários
                    </Typography>

                    <Button
                        variant="contained"
                        onClick={() => handleOpenCadastro()}
                        sx={{
                            width: { xs: '100%', sm: 'auto' },
                            minWidth: { xs: 'auto', sm: '200px' },
                            backgroundColor: '#20ac9c',
                            '&:hover': {
                                backgroundColor: '#167a6f'
                            }
                        }}
                    >
                        <AddIcon /> Novo Beneficiário
                    </Button>
                </Box>

                {/* Container da Tabela */}
                <TableContainer
                    component={Paper}
                    sx={{
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        position: 'relative',
                        overflow: 'auto',
                        height: {
                            xs: 'calc(90vh - 140px)',
                            sm: 'calc(90vh - 120px)',
                            md: 'calc(90vh - 120px)',
                            lg: 'calc(90vh - 120px)'
                        },
                        maxHeight: {
                            xs: 'calc(90vh - 140px)',
                            sm: 'calc(90vh - 120px)',
                            md: 'calc(90vh - 120px)',
                            lg: 'calc(90vh - 120px)'
                        },
                        width: '100%',
                        minWidth: { xs: '100%', sm: '600px' },
                        maxWidth: '100%',
                        boxShadow: (theme) => theme.palette.mode === 'dark'
                            ? '0 2px 8px rgba(0, 0, 0, 0.15)'
                            : '0 2px 8px rgba(0, 0, 0, 0.06)',
                        borderRadius: 2,
                        border: 'none'
                    }}
                >
                    <Table sx={{ minWidth: '100%', tableLayout: 'fixed' }}>
                        <TableHead sx={{
                            position: 'sticky',
                            top: 0,
                            zIndex: 10,
                            backgroundColor: (theme) => theme.palette.background.paper,
                            '& .MuiTableCell-root': {
                                backgroundColor: 'inherit',
                                borderBottom: '1px solid rgba(224, 224, 224, 1)'
                            }
                        }}>
                            <TableRow>
                                <TableCell sx={{ width: { xs: '30%', sm: '25%' } }}>
                                    <TableSortLabel
                                        active={orderBy === "st_nome"}
                                        direction={orderBy === "st_nome" ? order : "asc"}
                                        onClick={() => handleRequestSort("st_nome")}
                                    >
                                        Nome
                                    </TableSortLabel>
                                    <TextField
                                        name="st_nome"
                                        size="small"
                                        fullWidth
                                        value={filtros.st_nome}
                                        onChange={handleFiltroChange}
                                        placeholder="Filtrar por nome"
                                        sx={{ '& .MuiInputBase-input': { fontSize: '0.875rem' } }}
                                    />
                                </TableCell>
                                <TableCell sx={{ width: { xs: '25%', sm: '20%' } }}>
                                    <TableSortLabel
                                        active={orderBy === "st_cpf"}
                                        direction={orderBy === "st_cpf" ? order : "asc"}
                                        onClick={() => handleRequestSort("st_cpf")}
                                    >
                                        CPF
                                    </TableSortLabel>
                                    <TextField
                                        name="st_cpf"
                                        size="small"
                                        fullWidth
                                        value={filtros.st_cpf}
                                        onChange={handleFiltroChange}
                                        placeholder="Filtrar por CPF"
                                        sx={{ '& .MuiInputBase-input': { fontSize: '0.875rem' } }}
                                    />
                                </TableCell>
                                <TableCell sx={{ width: '15%' }}>Telefone</TableCell>
                                <TableCell sx={{ width: '15%', textAlign: 'center' }}>Dependentes</TableCell>
                                <TableCell sx={{ width: '10%', textAlign: 'center' }}>Status</TableCell>
                                <TableCell sx={{ width: '10%', textAlign: 'center' }}>Ações</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                                        <Preloader message="Carregando beneficiários..." />
                                    </TableCell>
                                </TableRow>
                            ) : beneficiariosFiltrados.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                                        <Typography color="text.secondary">Nenhum beneficiário encontrado</Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                beneficiariosFiltrados
                                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                    .map((beneficiario, index) => (
                                        <TableRow
                                            key={beneficiario.id_beneficiario}
                                            sx={{
                                                backgroundColor: (theme) => index % 2 === 0
                                                    ? (theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0, 0, 0, 0.02)')
                                                    : 'transparent',
                                                '&:hover': {
                                                    backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)'
                                                }
                                            }}
                                        >
                                            <TableCell sx={{ height: '40px', padding: '8px 16px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                {beneficiario.usuario?.st_nome}
                                            </TableCell>
                                            <TableCell sx={{ height: '40px', padding: '8px 16px' }}>
                                                {formatarCPF(beneficiario.st_cpf)}
                                            </TableCell>
                                            <TableCell sx={{ height: '40px', padding: '8px 16px' }}>
                                                {formatarTelefone(beneficiario.usuario?.st_telefone)}
                                            </TableCell>
                                            <TableCell align="center" sx={{ height: '40px', padding: '8px 16px' }}>
                                                <Chip
                                                    icon={<GroupIcon fontSize="small" />}
                                                    label={beneficiario.dependentes?.length || 0}
                                                    size="small"
                                                    variant="outlined"
                                                />
                                            </TableCell>
                                            <TableCell align="center" sx={{ height: '40px', padding: '8px 16px' }}>
                                                <Chip
                                                    label={beneficiario.bo_liberado ? "Ativo" : "Bloqueado"}
                                                    color={beneficiario.bo_liberado ? "success" : "error"}
                                                    size="small"
                                                />
                                            </TableCell>
                                            <TableCell align="center" sx={{ height: '40px', padding: '8px 16px' }}>
                                                <Tooltip title="Visualizar">
                                                    <IconButton size="small" onClick={() => visualizarBeneficiario(beneficiario)}>
                                                        <VisibilityIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Editar">
                                                    <IconButton size="small" onClick={() => handleOpenCadastro(beneficiario.id_beneficiario)}>
                                                        <EditIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Imprimir Contrato">
                                                    <IconButton size="small" onClick={() => handlePrint(beneficiario)}>
                                                        <PrintIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </TableCell>
                                        </TableRow>
                                    ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                {/* Paginação */}
                <TablePagination
                    component="div"
                    count={beneficiariosFiltrados.length}
                    page={page}
                    onPageChange={(event, newPage) => setPage(newPage)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(event) => {
                        setRowsPerPage(parseInt(event.target.value, 10));
                        setPage(0);
                    }}
                    rowsPerPageOptions={[5, 10, 25]}
                    labelRowsPerPage="Itens por página"
                    labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
                    sx={{
                        backgroundColor: (theme) => theme.palette.background.default,
                        borderTop: 'none',
                        width: '100%',
                        marginTop: 0
                    }}
                />
            </Box>

            {/* Dialog de Visualização */}
            <Dialog
                open={openView}
                onClose={fecharVisualizacao}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: (theme) => theme.palette.mode === 'dark' ? '#1B5E20' : '#E0F8E0', borderBottom: 1, borderColor: 'divider' }}>
                    Visualizar Beneficiário
                    <IconButton onClick={fecharVisualizacao}><CloseIcon /></IconButton>
                </DialogTitle>
                <DialogContent sx={{ padding: 3 }}>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
                        <TextField fullWidth label="Nome Completo" value={beneficiarioVisualizar?.usuario?.st_nome || ""} disabled />
                        <TextField fullWidth label="CPF" value={formatarCPF(beneficiarioVisualizar?.st_cpf)} disabled />
                        <TextField fullWidth label="Telefone" value={formatarTelefone(beneficiarioVisualizar?.usuario?.st_telefone)} disabled />
                        <TextField fullWidth label="Email" value={beneficiarioVisualizar?.usuario?.st_email || ""} disabled />
                        <TextField fullWidth label="Dependentes" value={beneficiarioVisualizar?.dependentes?.length || 0} disabled />
                        <TextField fullWidth label="Status" value={beneficiarioVisualizar?.bo_liberado ? "Ativo" : "Bloqueado"} disabled />
                    </Box>
                </DialogContent>
                <DialogActions sx={{ backgroundColor: (theme) => theme.palette.mode === 'dark' ? '#1B5E20' : '#E0F8E0', borderTop: 1, borderColor: 'divider', padding: 2 }}>
                    <Button onClick={fecharVisualizacao} variant="contained">FECHAR</Button>
                </DialogActions>
            </Dialog>

            {/* Dialog de Cadastro */}
            {openCadastro && (
                <CadastroBeneficiario
                    open={openCadastro}
                    onClose={handleCloseCadastro}
                    beneficiarioId={beneficiarioEditarId}
                    onSuccess={handleSaveSuccess}
                />
            )}
        </Box>
    );
}
